/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, August 21st 2022, 10:15:34 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { isEmpty } from "lodash";
import { APISERVICEPATHS, useAPIForm, UseAPIFormSpec } from "lib/api";
import {
  ActivationCondition,
  ActivationConditionValidationSchema,
  NextActivation,
  NextActivationValidationSchema,
} from "lib/schemas";
import { useDeleteOSItem } from "version-control/services";

/** NextActivations API */

const nextActivationsAPI = APISERVICEPATHS.nextActivations;

export const useDeleteNextActivation = () => useDeleteOSItem(nextActivationsAPI);

export interface UseNextActivationAPIFormSpec {
  nextActivationFetchId: UseAPIFormSpec<NextActivation>["apiInitialFetchId"];
  nextActivationDefaultValues: NextActivation;
  updateNextActivationFromAPICallback: UseAPIFormSpec<NextActivation>["setAPIFormResponse"];
  onSuccessfulAPISave: () => void;
}

export const useNextActivationAPIForm = ({
  nextActivationFetchId,
  nextActivationDefaultValues,
  updateNextActivationFromAPICallback,
  onSuccessfulAPISave,
}: UseNextActivationAPIFormSpec) => {
  const useAPIFormReturn = useAPIForm({
    apiServicePath: nextActivationsAPI,
    apiFormDataValidationSchema: NextActivationValidationSchema,
    apiFormDefaultValues: nextActivationDefaultValues,
    apiInitialFetchId: nextActivationFetchId,
    primaryKey: "id",
    setAPIFormResponse: updateNextActivationFromAPICallback,
    formResetDependencyArray: [
      nextActivationDefaultValues?.id,
      nextActivationDefaultValues?.task_definition,
      nextActivationDefaultValues?.ontrue,
    ],
  });

  const {
    useFormReturn: { reset },
  } = useAPIFormReturn;

  React.useEffect(() => {
    if (!isEmpty(nextActivationDefaultValues)) reset(nextActivationDefaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextActivationDefaultValues?.id, nextActivationDefaultValues?.task_definition]);

  React.useEffect(() => {
    if (useAPIFormReturn.isSuccessfulAPIDataSend) onSuccessfulAPISave?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAPIFormReturn.isSuccessfulAPIDataSend]);

  return useAPIFormReturn;
};

/** ActivationConditions API */

const activationConditionsAPI = APISERVICEPATHS.activationConditions;

export const useDeleteActivationCondition = () => useDeleteOSItem(activationConditionsAPI);

export interface UseActivationConditionAPIFormSpec {
  activationConditionFetchId: UseAPIFormSpec<ActivationCondition>["apiInitialFetchId"];
  activationConditionDefaultValues: ActivationCondition;
  updateActivationConditionFromAPICallback: UseAPIFormSpec<ActivationCondition>["setAPIFormResponse"];
}

export const useActivationConditionAPIForm = ({
  activationConditionFetchId,
  activationConditionDefaultValues,
  updateActivationConditionFromAPICallback,
}: UseActivationConditionAPIFormSpec) => {
  const useAPIFormReturn = useAPIForm({
    apiServicePath: activationConditionsAPI,
    apiFormDataValidationSchema: ActivationConditionValidationSchema,
    apiFormDefaultValues: activationConditionDefaultValues,
    apiInitialFetchId: activationConditionFetchId,
    primaryKey: "id",
    setAPIFormResponse: updateActivationConditionFromAPICallback,
  });

  return useAPIFormReturn;
};
